import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Plus, ArrowUpRight, ArrowDownRight, Trash2 } from "lucide-react";
import { api, formatXAF, formatDate } from "@/lib/api";
import { useEntity } from "@/lib/entity";
import Modal, { ConfirmDialog } from "@/components/Modal";

const CATEGORIES = ["Vente", "Achat fournisseur", "Salaire", "Loyer", "Fiscalité", "Autre"];

export default function Treasury() {
  const { currentEntityId, entities } = useEntity();
  const [data, setData] = useState({ entries: [], total_encaissements: 0, total_decaissements: 0, solde: 0 });
  const [loading, setLoading] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [confirmId, setConfirmId] = useState(null);
  const [form, setForm] = useState(initForm(currentEntityId));

  useEffect(() => { load(); }, [currentEntityId]);
  useEffect(() => { setForm(initForm(currentEntityId)); }, [currentEntityId]);

  async function load() {
    setLoading(true);
    try {
      const { data } = await api.get(`/treasury/${currentEntityId}`);
      setData(data);
    } finally { setLoading(false); }
  }

  async function submit(e) {
    e.preventDefault();
    await api.post("/treasury", { ...form, amount: parseFloat(form.amount) });
    setShowForm(false);
    setForm(initForm(currentEntityId));
    load();
  }

  async function remove(id) {
    await api.delete(`/treasury/${id}`);
    load();
  }

  const opEntities = entities.filter((e) => e.id !== "GROUPE");

  return (
    <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.35 }} className="space-y-6" data-testid="treasury-page">
      {/* KPIs */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <SummaryCard label="Encaissements" value={data.total_encaissements} color="ok" icon={ArrowUpRight} />
        <SummaryCard label="Décaissements" value={data.total_decaissements} color="warn" icon={ArrowDownRight} />
        <SummaryCard label="Solde" value={data.solde} color={data.solde >= 0 ? "citron" : "bad"} highlight />
      </div>

      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-heading text-2xl font-bold tracking-tight">Mouvements</h2>
          <p className="text-xs text-obsidian-400 mt-1">{data.entries.length} entrée(s)</p>
        </div>
        <button onClick={() => setShowForm(true)} className="btn-primary" data-testid="treasury-new-button">
          <Plus size={14} /> Nouveau mouvement
        </button>
      </div>

      <div className="card overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-obsidian-400 border-b border-line">
              <Th>Date</Th><Th>Entité</Th><Th>Type</Th><Th>Catégorie</Th><Th>Libellé</Th>
              <Th align="right">Montant</Th><Th></Th>
            </tr>
          </thead>
          <tbody>
            {loading && (<tr><td colSpan={7} className="p-8 text-center text-obsidian-500">Chargement…</td></tr>)}
            {!loading && data.entries.length === 0 && (
              <tr><td colSpan={7} className="p-12 text-center text-obsidian-500">
                Aucun mouvement pour l'instant.<br/>
                <button onClick={() => setShowForm(true)} className="mt-3 text-citron hover:underline text-sm">+ Ajouter le premier</button>
              </td></tr>
            )}
            {data.entries.map((r) => (
              <tr key={r.id} className="border-b border-line/50 hover:bg-obsidian-850 transition-colors" data-testid={`treasury-row-${r.id}`}>
                <Td><span className="font-mono text-xs">{formatDate(r.date)}</span></Td>
                <Td><span className="pill bg-obsidian-800 text-obsidian-100">{r.entity_id}</span></Td>
                <Td>
                  <span className={`pill ${r.type === "encaissement" ? "bg-ok/15 text-ok" : "bg-warn/15 text-warn"}`}>
                    {r.type}
                  </span>
                </Td>
                <Td>{r.category}</Td>
                <Td className="text-obsidian-200">{r.label}</Td>
                <Td align="right">
                  <span className={`font-mono tabular text-sm font-semibold ${r.type === "encaissement" ? "text-ok" : "text-warn"}`}>
                    {r.type === "encaissement" ? "+" : "−"} {formatXAF(r.amount)}
                  </span>
                </Td>
                <Td align="right">
                  <button onClick={() => setConfirmId(r.id)} className="btn-ghost h-7 w-7 p-0 text-obsidian-500 hover:text-bad" data-testid={`treasury-delete-${r.id}`}>
                    <Trash2 size={12} />
                  </button>
                </Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <Modal open={showForm} onClose={() => setShowForm(false)} title="Nouveau mouvement de trésorerie" subtitle="Enregistrer un encaissement ou décaissement">
        <form onSubmit={submit} className="space-y-4" data-testid="treasury-form">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Entité</label>
              <select className="input" value={form.entity_id} onChange={(e) => setForm({ ...form, entity_id: e.target.value })} required>
                {opEntities.map(e => <option key={e.id} value={e.id}>{e.name}</option>)}
              </select>
            </div>
            <div>
              <label className="label block">Date</label>
              <input type="date" className="input" value={form.date} onChange={(e) => setForm({ ...form, date: e.target.value })} required />
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Type</label>
              <select className="input" value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })}>
                <option value="encaissement">Encaissement</option>
                <option value="decaissement">Décaissement</option>
              </select>
            </div>
            <div>
              <label className="label block">Catégorie</label>
              <select className="input" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                {CATEGORIES.map(c => <option key={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div>
            <label className="label block">Libellé</label>
            <input className="input" placeholder="Description du mouvement" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} required />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="label block">Montant (XAF)</label>
              <input type="number" min="0" step="1" className="input font-mono" value={form.amount} onChange={(e) => setForm({ ...form, amount: e.target.value })} required />
            </div>
            <div>
              <label className="label block">Compte</label>
              <input className="input" value={form.account} onChange={(e) => setForm({ ...form, account: e.target.value })} />
            </div>
          </div>
          <div className="flex justify-end gap-2 pt-2">
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Annuler</button>
            <button type="submit" className="btn-primary" data-testid="treasury-submit">Enregistrer</button>
          </div>
        </form>
      </Modal>

      <ConfirmDialog
        open={!!confirmId}
        onClose={() => setConfirmId(null)}
        onConfirm={() => { remove(confirmId); }}
        title="Supprimer ce mouvement ?"
        message="Cette action est irréversible et sera tracée dans le journal d'audit."
        confirmLabel="Supprimer"
      />
    </motion.div>
  );
}

function initForm(entityId) {
  return {
    entity_id: entityId === "GROUPE" ? "ROPS" : entityId,
    date: new Date().toISOString().slice(0, 10),
    type: "encaissement",
    category: "Vente",
    label: "",
    amount: "",
    account: "Caisse Principale",
    notes: "",
  };
}

function SummaryCard({ label, value, color, icon: Icon, highlight }) {
  const colors = { ok: "text-ok", warn: "text-warn", citron: "text-citron", bad: "text-bad" };
  return (
    <div className={`card p-5 ${highlight ? "ring-1 ring-citron/30" : ""}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="overline">{label}</span>
        {Icon && <Icon size={14} className={colors[color]} />}
      </div>
      <div className={`font-mono text-2xl font-semibold tabular ${colors[color]}`}>{formatXAF(value)}</div>
    </div>
  );
}

function Th({ children, align = "left" }) {
  return <th className={`overline px-4 py-3 text-${align}`}>{children}</th>;
}

function Td({ children, align = "left", className = "" }) {
  return <td className={`px-4 py-3 text-${align} ${className}`}>{children}</td>;
}
